import { useCallback, useEffect, useState, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import clsx from 'clsx';
import { Filter, Table } from '../../../packages/ui/organisms/table';
import {
  Badge,
  badgeStyle,
  badgeType,
  btnType,
  Checkbox,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  IconButton,
  Icons,
  Menu,
  Text,
} from '../../../packages';
import { ProfileInfo } from '../../../packages/ui/templates/profile-info';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmationModal } from '../../../packages/ui/templates/confirmation-modal';
import { DROPDOWN_FILTER_COLLAPSABLE_TYPE } from '../../../packages/ui/organisms/table/filter';

import { showSnackbar } from '../../../redux/modules/snackbar';
import { BaseLayout } from '../../../layouts/base';
import { profileActionCreators } from '../../../redux/modules/profile';
import dayjs from '../../../utils/dayjs';

import ProviderFeeDetailDrawer from './provider-fee-detail-drawer';
import {
  multiSelectFilterOptionsMock,
  tableColumns,
  PROVIDER_FEE_FAST_FILTERS,
  TAB_KEY_TO_FILTER_VALUE,
} from './ProviderFee.constants';
import { useStyles } from './ProviderFee.styles';
import { Tabs } from '../../../packages/ui/atoms/tabs';
import { badgeSize } from '../../../packages';
import {
  downloadProviderFeeList,
  getProviderFeeList,
  getProviderFeeCounts,
  markAllSelectedProviderFeePaid,
  markAllSelectedProviderFeeRejected,
  markProviderFeePaid,
  markProviderFeeRejected,
} from '../../../services/provider-fee/providerFee.service';
import { providerFeeFilterOptions } from '../payments/Payments.constants';

const ProviderFee = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isPaidAll, setIsPaidAll] = useState(false);
  const providersList = useSelector(state => state.profile.providers2);
  const [filterChangeLoading, setFilterChangeLoading] = useState(false);
  const [resetMultiSelectFilter, setResetMultiSelectFilter] = useState(false);
  const [filterOptionsMock, setFilterOptionsMock] = useState([]);
  const [tableParams, setTableParams] = useState({
    search: {
      searchKey: '',
      filter: [],
      dateFilter: {
        startDate: '',
        endDate: '',
      },
    },
    pagination: { currentPage: 1, rowsPerPage: 10 },
    sorter: { direction: 'desc', column: '' },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [providerFeeList, setProviderFeeList] = useState([]);
  const [selectedProviderFeesIds, setSelectedProviderFeesIds] = useState([]);
  const [selectedFilter] = useState('All');
  const [previousSelectedFilter, setPreviousSelectedFilter] = useState('');
  const [tabType, setTabType] = useState(PROVIDER_FEE_FAST_FILTERS.ALL);
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [tabs, setTabs] = useState([
    { tabKey: PROVIDER_FEE_FAST_FILTERS.ALL, label: 'All', count: 0 },
    { tabKey: PROVIDER_FEE_FAST_FILTERS.PAID, label: 'Paid', count: 0 },
    { tabKey: PROVIDER_FEE_FAST_FILTERS.REJECTED, label: 'Rejected', count: 0 },
    { tabKey: PROVIDER_FEE_FAST_FILTERS.CASH, label: 'Cash', count: 0 },
    { tabKey: PROVIDER_FEE_FAST_FILTERS.INSURANCE, label: 'Insurance', count: 0 },
    { tabKey: PROVIDER_FEE_FAST_FILTERS.NO_SHOW, label: 'No show', count: 0 },
    { tabKey: PROVIDER_FEE_FAST_FILTERS.NO_CPT_CODES, label: 'No cpt codes', count: 0 },
    { tabKey: PROVIDER_FEE_FAST_FILTERS.WITH_CPT_CODES, label: 'With cpt codes', count: 0 },
  ]);
  const [countDetails, setCountDetails] = useState({
    totalFeePaid: 0,
    totalFeeSubmitted: 0,
    totalFeeRejected: 0,
    count: 0,
    totalFeeInsurance: 0,
    totalFeeCash: 0,
    totalFee: 0,
  });

  useEffect(() => {
    if (providersList?.length <= 0) dispatch(profileActionCreators.fetchAllProviders());
    void fetchCounts();
  }, []);

  const getFormattedData = data => {
    const formatProviderFees = data?.map(providerFee => ({
      ...providerFee,
      selected: {
        isChecked: false,
        _id: providerFee?._id,
        status: providerFee?.status,
      },
      member: {
        nickName:
          providerFee?.member?.firstName && providerFee?.member?.lastName
            ? `${providerFee?.member?.firstName || ''} ${providerFee?.member?.lastName || ''}`
            : providerFee?.member?.fullName ?? 'No Name',
        fullName: providerFee?.member?.uuid,
        profileImage: providerFee?.member?.profileImage,
        id: providerFee?.member?.id ?? providerFee?.memberId,
      },
      provider: {
        nickName: providerFee?.provider?.nickName,
        fullName: providerFee?.provider?.firstName
          ? `${providerFee?.provider?.firstName || ''} ${providerFee?.provider?.lastName || ''}`
          : 'N/A',
        profileImage: providerFee?.provider?.profileImage,
        role: providerFee?.provider?.designation || 'N/A',
        id: providerFee?.provider?.id,
      },
      amount: {
        amountPaid: providerFee?.amount,
        increased: providerFee.amount?.increase,
        changed: providerFee.amount?.changed,
      },
      insuranceProvider: {
        insuranceProvider: providerFee?.insuranceDetail?.payer || 'N/A',
        isNoShow: providerFee?.isNoShow || false,
      },
      paidAt: providerFee?.paidAt ?? null,
      actions: { ...providerFee },
    }));

    return formatProviderFees;
  };

  const fetchProviderFeeList = async () => {
    const multiselectFilters = tableParams.search?.multiSelectFilter || {};

    // Get statuses: prioritize manual filter selection, then tab selection
    let statuses = [];
    if (multiselectFilters?.Status?.length > 0) {
      statuses = multiselectFilters.Status;
    } else if (
      tabType &&
      tabType !== PROVIDER_FEE_FAST_FILTERS.ALL &&
      (tabType === PROVIDER_FEE_FAST_FILTERS.PAID || tabType === PROVIDER_FEE_FAST_FILTERS.REJECTED)
    ) {
      statuses = [TAB_KEY_TO_FILTER_VALUE[tabType]];
    }

    const states = multiselectFilters?.State?.length > 0 ? multiselectFilters?.State : [];
    const roles = multiselectFilters?.Role?.length > 0 ? multiselectFilters?.Role : [];
    const filterByPaidAt = multiselectFilters?.['Date filter preference']?.length > 0 || false;

    // Get payment type: prioritize manual filter selection, then tab selection
    let paymentType = null;
    const paymentMethod = multiselectFilters?.['Payment Method']?.[0];
    if (paymentMethod) {
      if (paymentMethod === 'Cash') {
        paymentType = 'CASH';
      } else if (paymentMethod === 'Insurance') {
        paymentType = 'INSURANCE';
      }
    } else if (tabType === PROVIDER_FEE_FAST_FILTERS.CASH) {
      paymentType = 'CASH';
    } else if (tabType === PROVIDER_FEE_FAST_FILTERS.INSURANCE) {
      paymentType = 'INSURANCE';
    }

    // Get boolean filters
    const onlyNoShow = tabType === PROVIDER_FEE_FAST_FILTERS.NO_SHOW;
    const withoutCptCodes = tabType === PROVIDER_FEE_FAST_FILTERS.NO_CPT_CODES;
    const withCptCodes = tabType === PROVIDER_FEE_FAST_FILTERS.WITH_CPT_CODES;
    const onlyMedicaid = paymentMethod === 'Medicaid';
    const withoutMedicaid = paymentMethod === 'Commercial';

    // const selectedProviders = multiselectFilters?.[DROPDOWN_FILTER_COLLAPSABLE_TYPE.PROVIDERS]?.map(
    //   provider => {
    //     return provider.id;
    //   },
    // );

    try {
      setIsLoading(true);
      const {
        pagination: { rowsPerPage, currentPage },
      } = tableParams;
      const queryParams = {
        // providerId: selectedProviders,
        page: currentPage,
        limit: rowsPerPage,
      };
      const body = {
        searchQuery: tableParams?.search?.searchKey,
        startDate: tableParams?.search?.dateFilter?.startDate
          ? dayjs(tableParams?.search?.dateFilter?.startDate)?.toISOString()
          : null,
        endDate: tableParams?.search?.dateFilter?.endDate
          ? dayjs(tableParams?.search?.dateFilter?.endDate)?.toISOString()
          : null,
        statuses,
        states,
        roles,
        providerIds: multiselectFilters[DROPDOWN_FILTER_COLLAPSABLE_TYPE.PROVIDERS]?.map(provider => {
          return provider.id;
        }),
        filterByPaidAt,
        paymentType,
        onlyNoShow,
        withoutCptCodes,
        withCptCodes,
        onlyMedicaid,
        withoutMedicaid,
      };

      const response = await getProviderFeeList(queryParams, body);
      setProviderFeeList(getFormattedData(response?.data?.data));
      setCountDetails(response?.data?.metadata);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedFilter !== previousSelectedFilter) {
      setPreviousSelectedFilter(selectedFilter);
    }
    setSelectedProviderFeesIds([]);
    setIsCheckAll(false);

    void fetchProviderFeeList();
  }, [tableParams, selectedFilter]);

  const markProviderFeePaidOnScreen = async data => {
    try {
      const response = await markProviderFeePaid({ id: data?._id });
      if (response.status === 200) {
        setIsCheckAll(false);
        setSelectedProviderFeesIds([]);
        await fetchProviderFeeList();
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: response?.data?.data?.message || 'Status updated successfully',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: response?.data?.data?.message || 'Something went wrong!Please try again !',
          }),
        );
      }
    } catch (error) {
      console.log(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Something went wrong! Please try again',
        }),
      );
    }
  };

  const updateSelectedProviderFeeIds = list => {
    const selectedArray = list
      .filter(item => item.selected.isChecked)
      .map(row => {
        return row.selected._id;
      });
    setSelectedProviderFeesIds(selectedArray);
  };
  const markProviderFeeRejectedOnScreen = async data => {
    try {
      const response = await markProviderFeeRejected({ id: data?._id });
      if (response?.status === 200) {
        setIsCheckAll(false);
        setSelectedProviderFeesIds([]);
        fetchProviderFeeList()
          .then(r => console.log(r))
          .catch(e => console.log(e));
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: response?.data?.data?.message || 'Status updated successfully',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: response?.data?.data?.message || 'Something went wrong! Please try again',
          }),
        );
      }
    } catch (error) {
      console.log(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Something went wrong! Please try again',
        }),
      );
    }
  };

  const confirmMultipleSelectAction = isMultiplePay => {
    setIsPaidAll(isMultiplePay);
    setShowConfirmModal(true);
  };

  const onMultipleSelectAction = async () => {
    try {
      let response;
      if (isPaidAll) {
        response = await markAllSelectedProviderFeePaid({ ids: selectedProviderFeesIds });
      } else {
        response = await markAllSelectedProviderFeeRejected({ ids: selectedProviderFeesIds });
      }
      if (response.status === 200) {
        setShowConfirmModal(false);
        setIsCheckAll(false);
        setSelectedProviderFeesIds([]);
        fetchProviderFeeList()
          .then(r => console.log(r))
          .catch(e => console.log(e));
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: response?.data?.data?.message || 'Status updated successfully',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: response?.data?.data?.message || 'Something went wrong!Please try again !',
          }),
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Something went wrong! Please try again',
        }),
      );
    }
  };

  const onRowClick = data => {
    setSelectedItem(data);
  };

  const onSelectAllClick = isSelectAll => {
    setIsCheckAll(isSelectAll);
    const currentCheck = isSelectAll;
    setProviderFeeList(prevState => {
      const updatedProviderFeeList = [...prevState];
      updatedProviderFeeList.forEach(item => {
        if (!['Paid', 'Rejected'].includes(item.status)) {
          item.selected.isChecked = currentCheck;
        }
      });
      updateSelectedProviderFeeIds(updatedProviderFeeList);
      return updatedProviderFeeList;
    });
  };

  const renderColumns = useCallback(() => {
    return tableColumns.map(column => {
      if (column.id === 'timeStamp') {
        return {
          ...column,
          renderCell: timeStamp => {
            return (
              <div className={classes.timeWrap}>
                <div className={classes.date}>{dayjs(timeStamp).format('MM/DD/YYYY')}</div>
                <div className={classes.time}>{dayjs(timeStamp).format('h:mm A')}</div>
              </div>
            );
          },
        };
      }
      if (column.id === 'member') {
        return {
          ...column,
          renderCell: ({ nickName, fullName, profileImage, id }) => (
            <ProfileInfo
              type="member"
              photo={profileImage}
              nickName={nickName || 'N/A'}
              fullName={fullName || 'N/A'}
              memberId={id}
            />
          ),
        };
      }
      if (column.id === 'provider') {
        return {
          ...column,
          renderCell: ({ profileImage, fullName, role, nickName, id }) => (
            <ProfileInfo
              type="provider"
              photo={profileImage}
              role={role}
              fullName={fullName || 'N/A'}
              nickName={nickName || 'N/A'}
              memberId={id}
            />
          ),
        };
      }
      if (column.id === 'amount') {
        return {
          ...column,
          renderCell: ({ amountPaid }) => <span>${amountPaid || '0'}</span>,
        };
      }
      if (column.id === 'selected') {
        return {
          ...column,
          renderCell: ({ isChecked, _id, status }) => (
            <Checkbox
              disabled={status === 'Paid' || status === 'Rejected'}
              checked={isChecked}
              onChange={event => {
                const currentCheck = event.target.checked;
                setProviderFeeList(prevState => {
                  const index = prevState.findIndex(item => item._id === _id);
                  const updatedProviderFeeList = [...prevState];
                  updatedProviderFeeList[index].selected.isChecked = currentCheck;
                  updateSelectedProviderFeeIds(updatedProviderFeeList);
                  return updatedProviderFeeList;
                });
              }}
            />
          ),
        };
      }
      if (column.id === 'insuranceProvider') {
        return {
          ...column,
          renderCell: ({ insuranceProvider, isNoShow }) => (
            <>
              {' '}
              <span>{insuranceProvider}</span>
              {isNoShow && (
                <Text weight={fontWeight.MEDIUM} className={classes.noShowLabel}>
                  No show
                </Text>
              )}
            </>
          ),
        };
      }
      if (column.id === 'state') {
        return {
          ...column,
          renderCell: state => <span>{state || 'N/A'}</span>,
        };
      }
      if (column.id === 'paidAt') {
        return {
          ...column,
          renderCell: paidAt => {
            return paidAt ? (
              <div className={classes.timeWrap}>
                <div className={classes.date}>{dayjs(paidAt).format('MMMM D, YYYY')}</div>
                <div className={classes.time}>{dayjs(paidAt).format('h:mm A')}</div>
              </div>
            ) : (
              '-'
            );
          },
        };
      }
      if (column.id === 'status') {
        return {
          ...column,
          renderCell: val => {
            return (
              <Badge
                variant={badgeType.OUTLINED}
                style={
                  val === 'Paid'
                    ? badgeStyle.RESOLVED
                    : val === 'Submitted' || val === 'Created'
                    ? badgeStyle.PRIMARY
                    : badgeStyle.HIGH
                }
                className={clsx(
                  classes.status,
                  { [classes.unpaidStatus]: val === 'Rejected' },
                  { [classes.submittedStatus]: val === 'Submitted' || val === 'Created' },
                )}
              >
                {val}
              </Badge>
            );
          },
        };
      }

      if (column.id === 'cptList') {
        return {
          ...column,
          renderCell: cptList => (
            <Box>
              {cptList?.length > 0
                ? cptList?.map((cpt, index) => cpt && <Box key={index}>{cpt?.cptCode}</Box>)
                : 'N/A'}
            </Box>
          ),
        };
      }
      if (column.id === 'actions') {
        return {
          ...column,
          renderCell: data =>
            (data?.status === 'Submitted' || data?.status === 'Created') && (
              <Menu
                icon="more"
                className={classes.menu}
                itemsWrapperClassName={classes.menuItemsWrapper}
                items={[
                  {
                    label: 'Mark as Paid',
                    onClick: async () => {
                      await markProviderFeePaidOnScreen(data);
                    },
                  },
                  {
                    label: 'Mark as Rejected',
                    onClick: async () => {
                      await markProviderFeeRejectedOnScreen(data);
                    },
                  },
                ]}
              />
            ),
        };
      }
      return column;
    });
  }, []);

  const onCloseDrawer = () => {
    setSelectedItem(null);
  };

  const renderStatusTotal = (status, value) => (
    <Box
      padding={2}
      sx={{
        background: colors.white,
        flex: '1 1 0',
        boxShadow:
          '0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: '8px',
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Text className={classes.label}>TOTAL FEES {status.toUpperCase()}</Text>
        <Badge
          className={clsx(
            classes.statusLabel,
            { [classes.unpaidStatus]: status !== 'Paid' },
            { [classes.submittedStatus]: status === 'Submitted' },
          )}
          variant={badgeType.OUTLINED}
          style={
            status === 'Paid'
              ? badgeStyle.RESOLVED
              : status === 'Submitted'
              ? badgeStyle.PRIMARY
              : badgeStyle.HIGH
          }
        >
          {status !== '' ? status : 'Total'}
        </Badge>
      </Box>
      <Text className={classes.statusFee}>
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value)}
      </Text>
    </Box>
  );

  const onExportClick = () => {
    const multiselectFilters = tableParams.search?.multiSelectFilter || {};
    const statuses = multiselectFilters?.Status?.length > 0 ? multiselectFilters?.Status : [];
    const states = multiselectFilters?.State?.length > 0 ? multiselectFilters?.State : [];
    const roles = multiselectFilters?.Role?.length > 0 ? multiselectFilters?.Role : [];
    const filterByPaidAt = multiselectFilters?.['Date filter preference']?.length > 0 || false;
    const queryParams = {
      download: true,
      page: 1,
      limit: countDetails?.count || 10000,
    };
    const body = {
      searchQuery: tableParams?.search?.searchKey,
      startDate: tableParams?.search?.dateFilter?.startDate
        ? dayjs(tableParams?.search?.dateFilter?.startDate)?.toISOString()
        : null,
      endDate: tableParams?.search?.dateFilter?.endDate
        ? dayjs(tableParams?.search?.dateFilter?.endDate)?.toISOString()
        : null,
      statuses,
      states,
      roles,
      providerIds:
        multiselectFilters[DROPDOWN_FILTER_COLLAPSABLE_TYPE.PROVIDERS]?.map(provider => {
          return provider.id;
        }) || [],
      filterByPaidAt,
    };

    setIsLoadingExport(true);
    downloadProviderFeeList(queryParams, body)
      .then(res => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.ms-excel',
        });

        saveAs(blob, 'Provider fee list.xlsx');
        setIsLoadingExport(false);
      })
      .catch(err => {
        console.warn(err);
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Error downloading!',
          }),
        );
        setIsLoadingExport(false);
      });
  };

  const closeConfirmation = () => {
    setShowConfirmModal(false);
  };

  const checkScrollability = useCallback(() => {
    const container = tabsContainerRef.current?.querySelector('.MuiTabs-flexContainer');
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const scrollTabs = useCallback(direction => {
    const container = tabsContainerRef.current?.querySelector('.MuiTabs-flexContainer');
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    checkScrollability();
    const container = tabsContainerRef.current?.querySelector('.MuiTabs-flexContainer');
    if (container) {
      container.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
      return () => {
        container.removeEventListener('scroll', checkScrollability);
        window.removeEventListener('resize', checkScrollability);
      };
    }
  }, [checkScrollability, tabs]);

  const renderTab = tab => {
    return {
      tabKey: tab.tabKey,
      label: tab.label || tab.tabKey,
      icon: (
        <Badge className={classes.countBadge} size={badgeSize.XSMALL}>
          {tab?.count ?? 0}
        </Badge>
      ),
      iconPosition: 'end',
    };
  };

  const fetchCounts = async () => {
    const res = await getProviderFeeCounts();
    const counts = res?.data?.data || [];
    const totalCountObj = counts.find(count => count._id === 'TOTAL');
    const total = totalCountObj?.count || 0;

    // Map API _id values (from counts API) to tab keys
    // Counts API _id matches tabKey for all tabs (all uppercase)
    const idToTabKeyMap = {
      PAID: PROVIDER_FEE_FAST_FILTERS.PAID,
      REJECTED: PROVIDER_FEE_FAST_FILTERS.REJECTED,
      CASH: PROVIDER_FEE_FAST_FILTERS.CASH,
      INSURANCE: PROVIDER_FEE_FAST_FILTERS.INSURANCE,
      NO_SHOW: PROVIDER_FEE_FAST_FILTERS.NO_SHOW,
      WITHOUT_CPT_CODES: PROVIDER_FEE_FAST_FILTERS.NO_CPT_CODES,
      WITH_CPT_CODES: PROVIDER_FEE_FAST_FILTERS.WITH_CPT_CODES,
    };

    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.tabKey === PROVIDER_FEE_FAST_FILTERS.ALL) {
          return {
            ...tab,
            count: total,
          };
        }
        const mappedId = Object.keys(idToTabKeyMap).find(key => idToTabKeyMap[key] === tab.tabKey);
        const countObj = counts.find(count => count._id === mappedId);
        return {
          ...tab,
          count: countObj ? countObj.count : 0,
        };
      });
    });
  };

  useEffect(() => {
    setFilterOptionsMock(multiSelectFilterOptionsMock(providersList));
    setResetMultiSelectFilter(true);
  }, [providersList]);

  useEffect(() => {
    setResetMultiSelectFilter(false);
  }, [tableParams]);

  useEffect(() => {
    setFilterChangeLoading(true);
    setTimeout(() => {
      setFilterChangeLoading(false);
    }, 100);
  }, [filterOptionsMock]);

  return (
    <BaseLayout>
      <ProviderFeeDetailDrawer
        open={!!selectedItem}
        onClose={onCloseDrawer}
        invoice={selectedItem}
        onMarkAsPaid={async () => {
          await markProviderFeePaidOnScreen(selectedItem);
        }}
        onMarkAsReject={async () => {
          await markProviderFeeRejectedOnScreen(selectedItem);
        }}
      />
      <Box className={classes.root}>
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ marginBottom: 5 }}>
          <Stack direction="row" alignItems="center" gap={2} sx={{ height: 48 }}>
            <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
              Provider Fee
            </Heading>
            <Badge className={classes.totalBadge} variant={badgeType.OUTLINED} style={badgeStyle.UNRELATED}>
              {countDetails?.count || '0'} total
            </Badge>
          </Stack>
          <Stack direction="row" gap={2}>
            <IconButton
              icon="dollar-square"
              variant={btnType.PRIMARY}
              className={classes.rejectBtn}
              onClick={() => confirmMultipleSelectAction(false)}
              disabled={selectedProviderFeesIds.length <= 0}
            >
              Reject Multiple
            </IconButton>
            <IconButton
              icon="dollar-square"
              variant={btnType.PRIMARY}
              className={classes.addBtn}
              onClick={() => confirmMultipleSelectAction(true)}
              disabled={selectedProviderFeesIds.length <= 0}
            >
              Pay Multiple
            </IconButton>
          </Stack>
        </Stack>
        <Box display="flex" flexDirection="column" mb={4} gap={0}>
          {/* <Box display="flex" mb={2} gap={2}>
            {renderStatusTotal('', countDetails?.totalFee)}
            {renderStatusTotal('Insurance', countDetails?.totalFeeInsurance)}
            {renderStatusTotal('Cash', countDetails?.totalFeeCash)}
          </Box> */}
          <Box display="flex" mb={2} gap={2}>
            {renderStatusTotal('Submitted', countDetails?.totalFeeSubmitted || 0)}
            {renderStatusTotal('Paid', countDetails?.totalFeePaid || 0)}
            {renderStatusTotal('Rejected', countDetails?.totalFeeRejected || 0)}
          </Box>
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'white' }}
          ref={tabsContainerRef}
        >
          {canScrollLeft && (
            <Box onClick={() => scrollTabs('left')} className={classes.scrollArrowButton}>
              <Icons
                glyph="caret-down"
                color={colors.neutral600}
                className={clsx(classes.scrollArrow, classes.scrollArrowLeft)}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Tabs
              value={tabType}
              onChange={value => {
                setTabType(value);
                setTableParams({
                  ...tableParams,
                  pagination: { ...tableParams.pagination, currentPage: 1 },
                  search: {
                    ...tableParams.search,
                    multiSelectFilter: {
                      ...tableParams.search?.multiSelectFilter,
                    },
                  },
                });
              }}
              options={tabs.map(renderTab)}
              className={classes.tabs}
            />
          </Box>
          {canScrollRight && (
            <Box onClick={() => scrollTabs('right')} className={classes.scrollArrowButton}>
              <Icons
                glyph="caret-down"
                color={colors.neutral600}
                className={clsx(classes.scrollArrow, classes.scrollArrowRight)}
              />
            </Box>
          )}
        </Box>
        <div className={classes.providerFeeList}>
          <ConfirmationModal
            open={showConfirmModal}
            onClose={closeConfirmation}
            onSave={onMultipleSelectAction}
            title="Pay/Reject Multiple Invoices"
            message="Are you sure?"
          />
          {filterChangeLoading ? (
            <div className={classes.loader}>
              <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
              Loading...
            </div>
          ) : (
            <Table
              searchProps={{
                placeholder: 'Search member and provider',
                filterProps: {
                  variant: Filter.tableFilterType.MULTIPLE,
                  options: providerFeeFilterOptions,
                  multiSelectOptions: filterOptionsMock,
                  dateFilter: {
                    startDate: tableParams?.search?.dateFilter?.startDate,
                    endDate: tableParams?.search?.dateFilter?.endDate,
                  },
                },
                resetMultiSelectFilter,
                exportProps: {
                  btnTitle: 'Export',
                  isLoadingExport,
                },
              }}
              onClickExport={onExportClick}
              gridProps={{
                columns: renderColumns(),
                data: providerFeeList || [],
                isLoading,
                onRowClick,
                isCheckAll,
                onSelectAllClick,
              }}
              paginationProps={{
                currentRows: providerFeeList?.length || 0,
                totalCount: countDetails?.count || 0,
                showRowsPerPage: true,
              }}
              value={tableParams}
              onChange={newValue => {
                setTableParams({
                  ...newValue,
                  search: {
                    ...newValue.search,
                    filter: newValue?.search?.filter.filter(
                      (item, pos) => newValue?.search?.filter?.indexOf(item) === pos,
                    ),
                  },
                });
              }}
            />
          )}
        </div>
      </Box>
    </BaseLayout>
  );
};

export { ProviderFee };
